import { useEffect, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../store/hooks';
import { selectCurrentApplication } from '../../store/slices/application.slice';
import {
  ChartBarIcon,
  UsersIcon,
  CursorArrowRaysIcon,
  ClockIcon,
  PlusIcon,
  ArrowTrendingUpIcon,
  GlobeAltIcon,
  SignalIcon,
} from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';
import MainLayout from '../../components/layout/MainLayout';
import TimeRangeSelector, { TimeRange } from '../../components/common/TimeRangeSelector';
import AutoRefreshToggle, { AutoRefreshConfig } from '../../components/common/AutoRefreshToggle';
import productAnalyticsService from '../../services/api/product-analytics.service';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from 'recharts';

const CHART_COLORS = ['#06b6d4', '#8b5cf6', '#10b981', '#f59e0b', '#ef4444', '#ec4899', '#6366f1', '#14b8a6'];

function toDateKey(ts: string | Date): string {
  const d = new Date(ts);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function generateAllDates(startMs: number, endMs: number): string[] {
  const dates: string[] = [];
  const current = new Date(startMs);
  current.setHours(0, 0, 0, 0);
  const end = new Date(endMs);
  end.setHours(23, 59, 59, 999);
  while (current <= end) {
    dates.push(toDateKey(current));
    current.setDate(current.getDate() + 1);
  }
  return dates;
}

const countryFlag = (code: string) => {
  if (!code || code.length !== 2) return '';
  return String.fromCodePoint(...[...code.toUpperCase()].map(c => 0x1F1E6 - 65 + c.charCodeAt(0)));
};

export default function AnalyticsOverviewPage() {
  const { t } = useTranslation('analytics');
  const selectedAppId = useAppSelector(selectCurrentApplication);
  const [timeRange, setTimeRange] = useState<TimeRange>({
    startTime: Date.now() - 7 * 24 * 60 * 60 * 1000,
    endTime: Date.now(),
    rangeType: 'preset',
    presetValue: '7d',
  });
  const [autoRefresh, setAutoRefresh] = useState<AutoRefreshConfig>({ enabled: false, interval: 30000 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  // Data state
  const [stats, setStats] = useState({ totalUsers: 0, activeUsers: 0, newUsers: 0, totalEvents: 0 });
  const [topEvents, setTopEvents] = useState<Array<{ eventName: string; count: number }>>([]);
  const [timeseries, setTimeseries] = useState<Array<{ timestamp: string; count: number; uniqueUsers: number }>>([]);
  const [realtimeData, setRealtimeData] = useState({ activeUsers: 0, eventsPerMinute: 0, topPages: [] as Array<{ path: string; activeUsers: number }> });
  const [deviceData, setDeviceData] = useState<Array<{ deviceType: string; sessions: number; uniqueUsers: number }>>([]);
  const [browserData, setBrowserData] = useState<Array<{ browser: string; sessions: number; uniqueUsers: number }>>([]);
  const [trafficData, setTrafficData] = useState<Array<{ source: string; pageViews: number; sessions: number }>>([]);
  const [topPages, setTopPages] = useState<Array<{ path: string; views: number; uniqueSessions: number; avgDuration: number; avgScrollDepth: number; avgLcp: number }>>([]);
  const [countries, setCountries] = useState<Array<{ country: string; sessions: number; uniqueUsers: number }>>([]);

  const appId = selectedAppId || 'all';
  const startISO = new Date(timeRange.startTime).toISOString();
  const endISO = new Date(timeRange.endTime).toISOString();

  const currentInterval = useMemo(() => {
    const daysDiff = (timeRange.endTime - timeRange.startTime) / (1000 * 60 * 60 * 24);
    return daysDiff <= 1 ? 'hour' as const : daysDiff <= 30 ? 'day' as const : daysDiff <= 180 ? 'week' as const : 'month' as const;
  }, [timeRange]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [overview, ts, rt, devices, browsers, traffic, pages, geo] = await Promise.allSettled([
        productAnalyticsService.getAnalyticsOverview(appId, startISO, endISO),
        productAnalyticsService.getAggregatedTimeseries(appId, currentInterval, startISO, endISO),
        productAnalyticsService.getRealtimeMetrics(appId),
        productAnalyticsService.getDeviceBreakdown(appId, startISO, endISO),
        productAnalyticsService.getBrowserBreakdown(appId, startISO, endISO),
        productAnalyticsService.getTrafficSources(appId, startISO, endISO),
        productAnalyticsService.getTopPages(appId, startISO, endISO),
        productAnalyticsService.getSessionsByCountry(appId, startISO, endISO),
      ]);

      if (overview.status === 'fulfilled') {
        const d = overview.value;
        setStats({ totalUsers: d.totalUsers || 0, activeUsers: d.activeUsers || 0, newUsers: d.newUsers || 0, totalEvents: d.totalEvents || 0 });
        setTopEvents((d.topEvents || []).map((e: any) => ({ eventName: e.name || e.eventName, count: e.count })));
      }
      if (ts.status === 'fulfilled') setTimeseries(ts.value || []);
      if (rt.status === 'fulfilled') setRealtimeData(rt.value);
      if (devices.status === 'fulfilled') setDeviceData(devices.value || []);
      if (browsers.status === 'fulfilled') setBrowserData(browsers.value || []);
      if (traffic.status === 'fulfilled') setTrafficData(traffic.value || []);
      if (pages.status === 'fulfilled') setTopPages((pages.value || []).slice(0, 10));
      if (geo.status === 'fulfilled') setCountries((geo.value || []).slice(0, 10));

      setLastUpdated(new Date());
    } catch (err) {
      console.error('Failed to load analytics data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  }, [appId, startISO, endISO, currentInterval]);

  useEffect(() => { loadData(); }, [loadData]);

  // Realtime auto-refresh (10s)
  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        const rt = await productAnalyticsService.getRealtimeMetrics(appId);
        setRealtimeData(rt);
      } catch {}
    }, 10000);
    return () => clearInterval(interval);
  }, [appId]);

  // Auto-refresh effect
  useEffect(() => {
    if (!autoRefresh.enabled) return;
    const interval = setInterval(() => {
      if (timeRange.rangeType === 'preset' && timeRange.presetValue) {
        const endTime = Date.now();
        const ranges: Record<string, number> = { '15m': 15*60000, '1h': 3600000, '6h': 6*3600000, '12h': 12*3600000, '24h': 86400000, '7d': 7*86400000, '30d': 30*86400000 };
        setTimeRange({ ...timeRange, startTime: endTime - (ranges[timeRange.presetValue] || ranges['7d']), endTime });
      } else { loadData(); }
    }, autoRefresh.interval);
    return () => clearInterval(interval);
  }, [autoRefresh, timeRange, loadData]);

  const fmt = (n: number) => { if (n >= 1e6) return `${(n/1e6).toFixed(1)}M`; if (n >= 1e3) return `${(n/1e3).toFixed(1)}K`; return n.toString(); };
  const fmtMs = (ms: number) => { if (ms >= 1000) return `${(ms/1000).toFixed(1)}s`; return `${Math.round(ms)}ms`; };

  // Gap-fill chart data so every date in the range is represented
  const chartData = useMemo(() => {
    const allDates = generateAllDates(timeRange.startTime, timeRange.endTime);
    const dataMap = new Map<string, { count: number; uniqueUsers: number }>();
    timeseries.forEach(d => {
      dataMap.set(toDateKey(d.timestamp), { count: d.count, uniqueUsers: d.uniqueUsers });
    });
    return allDates.map(date => ({
      timestamp: date,
      count: dataMap.get(date)?.count || 0,
      uniqueUsers: dataMap.get(date)?.uniqueUsers || 0,
    }));
  }, [timeseries, timeRange.startTime, timeRange.endTime]);

  const formatXAxisTick = (v: string) => {
    try {
      const d = new Date(v);
      if (isNaN(d.getTime())) return v;
      if (currentInterval === 'hour') return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
      return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    } catch { return v; }
  };

  const devicePieData = deviceData.map(d => ({ name: d.deviceType || 'Unknown', value: d.sessions }));
  const browserPieData = browserData.slice(0, 6).map(d => ({ name: d.browser, value: d.sessions }));
  const trafficPieData = trafficData.slice(0, 6).map(d => ({ name: d.source, value: d.pageViews }));

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{t('overview.title')}</h1>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">{t('overview.subtitle')}</p>
          </div>
        </div>

        {/* Time Range Selector */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <TimeRangeSelector value={timeRange} onChange={setTimeRange} />
        </div>

        {/* Auto Refresh Toggle */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <AutoRefreshToggle value={autoRefresh} onChange={setAutoRefresh} lastUpdated={lastUpdated} />
        </div>

        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4">
            <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
          </div>
        )}

        {/* Row 1 — 5 Stat Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {/* Active Users Now */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{t('overview.activeUsersNow')}</p>
                <div className="flex items-center mt-2 space-x-2">
                  <span className="relative flex h-3 w-3">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
                  </span>
                  <p className="text-3xl font-bold text-gray-900 dark:text-white">{fmt(realtimeData.activeUsers)}</p>
                </div>
                <p className="text-xs text-gray-500 mt-1">{realtimeData.eventsPerMinute} {t('overview.eventsPerMinute')}</p>
              </div>
              <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                <SignalIcon className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </div>

          {/* Total Events */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{t('overview.totalEvents')}</p>
                <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">{fmt(stats.totalEvents)}</p>
              </div>
              <div className="p-3 bg-cyan-50 dark:bg-cyan-900/20 rounded-lg">
                <ChartBarIcon className="w-6 h-6 text-cyan-600" />
              </div>
            </div>
          </div>

          {/* Total Users */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{t('overview.totalUsers')}</p>
                <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">{fmt(stats.totalUsers)}</p>
              </div>
              <div className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                <UsersIcon className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </div>

          {/* Active Users */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{t('overview.activeUsers')}</p>
                <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">{fmt(stats.activeUsers)}</p>
              </div>
              <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                <CursorArrowRaysIcon className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </div>

          {/* New Users */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{t('overview.newUsers')}</p>
                <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">{fmt(stats.newUsers)}</p>
              </div>
              <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
                <ClockIcon className="w-6 h-6 text-amber-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Row 2 — Event Volume Chart */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">{t('overview.eventVolume')}</h2>
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id="ovColorCount" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#06b6d4" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#06b6d4" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="ovColorUsers" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                <XAxis dataKey="timestamp" tickFormatter={formatXAxisTick} className="text-xs" interval="preserveStartEnd" />
                <YAxis className="text-xs" allowDecimals={false} />
                <Tooltip
                  contentStyle={{ backgroundColor: 'rgba(17,24,39,0.9)', border: 'none', borderRadius: '8px', color: '#fff' }}
                  labelFormatter={formatXAxisTick}
                />
                <Area type="monotone" dataKey="count" name={t('overview.totalEvents')} stroke="#06b6d4" fill="url(#ovColorCount)" strokeWidth={2} />
                <Area type="monotone" dataKey="uniqueUsers" name={t('overview.uniqueUsers')} stroke="#8b5cf6" fill="url(#ovColorUsers)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">{t('overview.noEventsFound')}</div>
          )}
        </div>

        {/* Row 3 — Top Events + Top Pages */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Top Events */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{t('overview.topEvents')}</h2>
              <Link to="/analytics/events" className="text-sm text-cyan-600 hover:text-cyan-700 font-medium">{t('common.showAll')} →</Link>
            </div>
            <div className="space-y-3">
              {topEvents.length === 0 ? (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">{t('overview.noEventsFound')}</div>
              ) : topEvents.slice(0, 8).map((event, i) => {
                const maxCount = topEvents[0]?.count || 1;
                return (
                  <div key={event.eventName} className="flex items-center space-x-3">
                    <span className="w-6 h-6 flex items-center justify-center text-xs font-bold rounded-full bg-cyan-100 dark:bg-cyan-900/30 text-cyan-600">{i+1}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-sm font-medium text-gray-900 dark:text-white truncate">{event.eventName}</span>
                        <span className="text-sm text-gray-500 ml-2">{fmt(event.count)}</span>
                      </div>
                      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
                        <div className="bg-cyan-500 h-1.5 rounded-full" style={{ width: `${(event.count/maxCount)*100}%` }}></div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Top Pages */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{t('overview.topPages')}</h2>
              <Link to="/analytics/pages" className="text-sm text-cyan-600 hover:text-cyan-700 font-medium">{t('common.showAll')} →</Link>
            </div>
            <div className="space-y-3">
              {topPages.length === 0 ? (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">{t('common.noData')}</div>
              ) : topPages.slice(0, 8).map((page, i) => (
                <div key={page.path} className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-gray-700 last:border-0">
                  <div className="flex items-center space-x-3 min-w-0">
                    <span className="w-6 h-6 flex items-center justify-center text-xs font-bold rounded-full bg-purple-100 dark:bg-purple-900/30 text-purple-600">{i+1}</span>
                    <span className="text-sm font-medium text-gray-900 dark:text-white truncate">{page.path}</span>
                  </div>
                  <div className="flex items-center space-x-4 text-sm text-gray-500 shrink-0">
                    <span>{fmt(page.views)} {t('overview.views')}</span>
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${page.avgLcp <= 2500 ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : page.avgLcp <= 4000 ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'}`}>{fmtMs(page.avgLcp)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Row 4 — Three PieCharts */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Device Types */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-semibold text-gray-900 dark:text-white">{t('overview.deviceTypes')}</h2>
              <Link to="/analytics/devices" className="text-xs text-cyan-600 hover:text-cyan-700">{t('common.showAll')} →</Link>
            </div>
            {devicePieData.length > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={devicePieData} cx="50%" cy="50%" innerRadius={45} outerRadius={75} dataKey="value" paddingAngle={2}>
                    {devicePieData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                  </Pie>
                  <Tooltip />
                  <Legend iconSize={10} wrapperStyle={{ fontSize: '12px' }} />
                </PieChart>
              </ResponsiveContainer>
            ) : <div className="text-center py-8 text-gray-500 text-sm">{t('common.noData')}</div>}
          </div>

          {/* Browsers */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-semibold text-gray-900 dark:text-white">{t('overview.browsers')}</h2>
              <Link to="/analytics/devices" className="text-xs text-cyan-600 hover:text-cyan-700">{t('common.showAll')} →</Link>
            </div>
            {browserPieData.length > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={browserPieData} cx="50%" cy="50%" innerRadius={45} outerRadius={75} dataKey="value" paddingAngle={2}>
                    {browserPieData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                  </Pie>
                  <Tooltip />
                  <Legend iconSize={10} wrapperStyle={{ fontSize: '12px' }} />
                </PieChart>
              </ResponsiveContainer>
            ) : <div className="text-center py-8 text-gray-500 text-sm">{t('common.noData')}</div>}
          </div>

          {/* Traffic Sources */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-semibold text-gray-900 dark:text-white">{t('overview.trafficSources')}</h2>
              <Link to="/analytics/traffic" className="text-xs text-cyan-600 hover:text-cyan-700">{t('common.showAll')} →</Link>
            </div>
            {trafficPieData.length > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={trafficPieData} cx="50%" cy="50%" innerRadius={45} outerRadius={75} dataKey="value" paddingAngle={2}>
                    {trafficPieData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                  </Pie>
                  <Tooltip />
                  <Legend iconSize={10} wrapperStyle={{ fontSize: '12px' }} />
                </PieChart>
              </ResponsiveContainer>
            ) : <div className="text-center py-8 text-gray-500 text-sm">{t('common.noData')}</div>}
          </div>
        </div>

        {/* Row 5 — Countries + Quick Actions */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Top Countries */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{t('overview.topCountries')}</h2>
              <Link to="/analytics/geography" className="text-sm text-cyan-600 hover:text-cyan-700 font-medium">{t('common.showAll')} →</Link>
            </div>
            {countries.length === 0 ? (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">{t('common.noData')}</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                      <th className="pb-2 font-medium">Country</th>
                      <th className="pb-2 font-medium text-right">{t('overview.sessions')}</th>
                      <th className="pb-2 font-medium text-right">{t('overview.uniqueUsers')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {countries.map(c => (
                      <tr key={c.country} className="border-b border-gray-100 dark:border-gray-700/50 last:border-0">
                        <td className="py-2"><span className="mr-2">{countryFlag(c.country)}</span>{c.country}</td>
                        <td className="py-2 text-right font-medium text-gray-900 dark:text-white">{fmt(c.sessions)}</td>
                        <td className="py-2 text-right text-gray-500">{fmt(c.uniqueUsers)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Quick Actions */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">{t('overview.quickActions.title')}</h2>
            <div className="space-y-3">
              <Link to="/analytics/funnels/new" className="flex items-center justify-between p-3 bg-cyan-50 dark:bg-cyan-900/20 text-cyan-700 dark:text-cyan-400 rounded-lg hover:bg-cyan-100 dark:hover:bg-cyan-900/30 transition-colors">
                <span className="font-medium">{t('overview.quickActions.createFunnel')}</span>
                <PlusIcon className="w-5 h-5" />
              </Link>
              <Link to="/analytics/events" className="flex items-center justify-between p-3 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 rounded-lg hover:bg-green-100 dark:hover:bg-green-900/30 transition-colors">
                <span className="font-medium">{t('overview.quickActions.exploreEvents')}</span>
                <ArrowTrendingUpIcon className="w-5 h-5" />
              </Link>
              <Link to="/analytics/devices" className="flex items-center justify-between p-3 bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-400 rounded-lg hover:bg-purple-100 dark:hover:bg-purple-900/30 transition-colors">
                <span className="font-medium">{t('devices.title')}</span>
                <ArrowTrendingUpIcon className="w-5 h-5" />
              </Link>
              <Link to="/analytics/traffic" className="flex items-center justify-between p-3 bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 rounded-lg hover:bg-amber-100 dark:hover:bg-amber-900/30 transition-colors">
                <span className="font-medium">{t('traffic.title')}</span>
                <GlobeAltIcon className="w-5 h-5" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
}
